// @ts-strict-ignore

import type { Direction } from "@aviary/types";
import { useAnalyticsLogEventMutation } from "@shared/data/mutations/AnalyticsLogEventMutation/AnalyticsLogEvent.mutation";
import { useFullStorySessionUrl } from "@shared/hooks/useFullStorySessionUrl/useFullStorySessionUrl";
import { useUnauthUrlFields } from "@shared/hooks/useUnauthUrlFields/useUnauthUrlFields";
import type { DocumentEventDocType } from "@shared/types/DocumentEvent";
import type { MultiCartType } from "@shared/types/MultiCartType";
import type { PurchaseType } from "@shared/types/PurchaseType";
import type { SourcePlatform } from "@shared/types/SourcePlatform";
import type { StorePlatform } from "@shared/types/StorePlatform";
import type { VimeoEmbedUrl, VimeoPlayStatus } from "@shared/types/vimeo";
import type { YouTubeNoCookieUrl, YouTubePlayStatus } from "@shared/types/youtube";
import { getEHRInfoState } from "@shared/utils/ehrInfoStore/ehrInfoStore";

import type { AnalyticsEvent } from "./AnalyticsEvent";
import type { CollectionsEntryPoint } from "./CollectionsAnalyticsEntryPoint";
import type { EmersonEntryPoint } from "./EmersonAnalyticsEntryPoint";
import type { PatientEntryPoint } from "./PatientAnalyticsEntryPoint";
import type {
  LabAddPanelToPlanEntryPoint,
  LabsInterpretationEntryPoint,
  LabTestFavoritesEntryPoint,
  LabViewPlanEntryPoint,
  StoreActionEntryPoint,
  StoreEntryPoint,
  TemplateApplyEntryPoint,
} from "./StoreAnalyticsEntryPoint";
import type { StorefrontsEntryPoint } from "./StorefrontsAnalyticsEntryPoint";
import type { UnauthEntryPoint } from "./UnauthAnalyticsEntryPoint";

type AnalyticsPayload = {
  smart_ingredient_count?: number;
  smart_ingredient_dosages_changed?: number;
  manual_ingredient_count?: number;
  red_interactions_count?: number;
  depletions_count?: number;
  section_type?: string;
  suggested_ingredient_name?: string;
  indication_name?: string;
  onboarding_step?: string;
  practitioner_id?: string;
  practitioner?: string; // practitioner name
  clerk_id?: string;
  default_variant?: string;
  product_status?: string;
  origin_product_status?: string;
  brand_name?: string;
  entry_point?:
    | StoreEntryPoint
    | PatientEntryPoint
    | EmersonEntryPoint
    | UnauthEntryPoint
    | StorefrontsEntryPoint
    | StoreActionEntryPoint
    | LabAddPanelToPlanEntryPoint
    | LabTestFavoritesEntryPoint
    | LabsInterpretationEntryPoint
    | LabViewPlanEntryPoint
    | TemplateApplyEntryPoint
    | CollectionsEntryPoint;
  explore_subheader_1?: string;
  explore_subheader_2?: string;
  explore_subheader_3?: string;
  patient_id?: string;
  protocol_plan_id?: string;
  order_id?: string;
  shipping_address_id?: string;
  store_id?: string;
  number_of_items?: number;
  number_of_products?: number;
  number_of_autoship_subscriptions?: number;
  treatment_plan_id?: string;
  treatment_plan_template_id?: string;
  multipatient_plan_id?: string;
  template_name?: string;
  content_type?: string;
  content_name?: string;
  content_tags?: string;
  daily_pack_name?: string;
  daily_pack_sku?: string;
  update_type?: string | string[];
  update_value?: string;
  has_daily_pack?: boolean;
  severity?: string;
  sku?: string;
  sku_list?: string[];
  product_id_list?: string[];
  discount?: number;
  current_onboarding_step?: string;
  account_margin_type?: string;
  account_modality?: string;
  practitioner_modality?: string;
  margin?: number;
  previous_margin?: number;
  error_message?: string;
  error_type?: "validation" | "network";
  taxonomy_code?: string;
  attribute?: "view_integration" | "dismiss";
  url?: string;
  shareable_type?: string;
  lab_id?: string;
  lab_count?: number;
  lab_test_ids?: number[];
  lab_test_id?: string;
  lab_panel_id?: string;
  lab_recommendation_id?: string;
  has_message?: boolean;
  catalog_permission?: "open" | "closed";
  modification_type?: string;
  modification_value?: string;
  fields_changed?: string[];
  page_version?: string;
  cart_count?: number;
  search_rank?: number;
  group_search_rank?: number;
  organic_search_rank?: number;
  first_result_is_top_pick?: boolean;
  recent_search_rank?: number;
  search_query_type?: string;
  search_query?: string;
  search_session_id?: string;
  brand?: string;
  brand_list?: string[];
  contains_store_logo?: boolean;
  contains_hemp?: boolean;
  form_version?: string;
  signin_method?: string;
  has_treatment_plan?: boolean;
  automatic_event?: boolean;
  product_id?: string;
  search_version?: number;
  event_message?: string;
  from_wellevate?: boolean;
  status_of_products?: string[];
  page_url?: string;
  target_url?: string;
  origin_url?: string;
  platform_select?: SourcePlatform.FULLSCRIPT | SourcePlatform.WELLEVATE;
  store_platform?: StorePlatform.FULLSCRIPT | StorePlatform.EMERSON;
  rx_count?: number;
  sell_price?: string;
  total_attachments_count?: number;
  recs_with_reason_count?: number;
  category_type?: string;
  quantity?: number;
  status_of_product?: string;
  autoship_frequency?: string;
  autoship_frequencies?: string[];
  autoship_frequency_selected?: string;
  autoship_discount?: number;
  state_province?: string;
  delivery_method?: string;
  imat_attachments_count?: number;
  total_attachments?: string[];
  rx_items?: string[];
  address_selected?: string;
  ignore_address_verification?: boolean;
  selection_type?: string;
  patient_count?: number;
  dtp_cart_count?: number;
  filter?: string;
  filter_type?: string;
  patient_panel?: string;
  patient_labs?: string;
  patient_supplements?: string;
  number_of_pracs?: string;
  email?: string;
  uuid?: string;
  onboarding_segment_experiment?: string;
  account_discount?: number;
  account_business_type?: string;
  token_status?: string;
  favourite_category?: string;
  favorites_category_types?: string[];
  favorites_category_count?: number;
  filter_value?: string;
  action_type?: string;
  entrypoint?: string;
  page_URL?: string;
  daily_pack_filter_value?: string;
  referrer?: string;
  resource_name?: string;
  resource_type?: string;
  migration_option?: string;
  user_id?: string;
  source_account_id?: string;
  from_plan_type?: string;
  to_plan_type?: string;
  restricted_brand_error?: boolean;
  has_labs?: boolean;
  plan_type?: string;
  plan_id?: string;
  sent_welcome_email?: boolean;
  tab_selected?: "all_orders" | "not_purchased" | "results_available" | "action_needed";
  access_link_type?: string;
  staff_count?: number;
  plan_access?: string;
  protocol_id?: string;
  protocol_name?: string;
  used_template?: boolean;
  used_message_template?: boolean;
  template_owner?: string;
  monthly_cost?: number;
  has_alternative_product_options?: boolean;
  origin_sku?: string;
  origin_sku_repurchase_rate?: number;
  origin_sku_monthly_unit_cost?: number;
  origin_brand_name?: string;
  first_alternative_product_sku?: string;
  first_alternative_product_repurchase_rate?: number;
  first_alternative_product_monthly_unit_cost?: number;
  second_alternative_product_sku?: string;
  second_alternative_product_repurchase_rate?: number;
  second_alternative_product_monthly_unit_cost?: number;
  third_alternative_product_sku?: string;
  third_alternative_product_repurchase_rate?: number;
  third_alternative_product_monthly_unit_cost?: number;
  smart_suggestion_skus?: string[];
  smart_suggestion_brands?: string[];
  practitioner_selected_skus?: string[];
  practitioner_selected_brands?: string[];
  metric_type?: string;
  alternative_product_rank?: number;
  selected_delivery?: string;
  from_breadcrumb_path?: string;
  to_breadcrumb_path?: string;
  action?: string;
  response_action?: string;
  new_plan?: boolean;
  patient_intake_status?: string;
  supplement_count?: number;
  intake_record_id?: string;
  product_comparison_tag?: string[];
  purchase_type?: PurchaseType.ONE_TIME | PurchaseType.AUTOSHIP;
  is_prop_65?: boolean;
  accordion_headers?: string[];
  accordion_header?: string;
  section?: string;
  menu_type?: string;
  menu_category?: string;
  supplement_plan_id?: string;
  patient_intake_id?: string;
  product_type?: string;
  theme_target?: "light" | "dark";
  promotion_active?: boolean;
  is_restricted?: boolean;
  is_accessible?: boolean;
  currently_taking?: boolean;
  has_patient?: boolean;
  vote_type?: string;
  feedback_type?: string;
  current_shipping_method?: string;
  current_shipping_cost?: string;
  previous_shipping_method?: string;
  previous_shipping_cost?: string;
  contains_heat_sensitive_item?: boolean;
  agreement_type?: MapAgreementType;
  can_add_to_plan?: boolean;
  category_name?: string;
  subcategory_name?: string;
  autoship_subscription_id?: string;
  autoship_subscription_ids?: string[];
  searchRank?: number;
  number_of_suggestions?: number;
  organicSearchRank?: number;
  searchQueryType?: string;
  result_count?: number;
  total_item_count?: number;
  original_brand_name?: string;
  original_product_sku?: string;
  suggested_product_sku?: string;
  optimization_category?: string;
  original_product_brand?: string;
  suggested_product_brand?: string;
  to_staff_count?: number;
  from_staff_count?: number;
  to_staff_access_type?: string;
  from_staff_access_type?: string;
  from_access_type?: string;
  to_access_type?: string;
  from_patient_count?: number;
  to_patient_count?: number;
  from_practitioner_id?: string;
  to_practitioner_id?: string;
  unassigned_patient_count?: number;
  fullstory_session_url?: string;
  alternative_product_type?: string;
  product_details_footer_section?: string;
  visible_to_patients?: boolean;
  is_substitute_product?: boolean;
  auth_and_capture?: boolean;
  sent_patient_email?: boolean;
  substitute_skus_added?: string[];
  substitute_brands_added?: string[];
  substitute_skus_removed?: string[];
  substitute_brands_removed?: string[];
  medication_count?: number;
  is_swapped_product?: boolean;
  demographics_count?: number;
  health_areas_count?: number;
  manual_medication_count?: number;
  diagnostics_count?: number;
  is_suggested_ingredient?: boolean;
  searched_conditions_count?: number;
  all_items_checkbox_unchecked?: boolean;
  default_payment_method?: boolean;
  default_shipping_address?: boolean;
  use_as_auto_refill?: boolean;
  availability_type?: string;
  number_of_oos_products?: number;
  number_of_backordered_products?: number;
  number_of_discontinued_products?: number;
  number_of_tbd_products?: number;
  sort_type?: string;
  sort_by?: string;
  is_fs_tested?: boolean;
  is_fs_preferred?: boolean;
  favourites_category?: string[];
  is_favourite?: boolean;
  menu_version?: number;
  entity_type?: string;
  entity_id?: string;
  entity_name?: string;
  typeahead_query?: string;
  typeahead_query_length?: number;
  survey_sent_at?: string;
  survey_completed_at?: string;
  interaction_type?: "view_all" | "view_category";
  algorithm_version?: string;
  has_active_plan_interactions?: boolean;
  title?: string;
  open_direction?: Direction;
  name?: string;
  filter_source?: FilterSource;
  label?: string;
  overlay_name?: string;
  button_type?: "link" | "button";
  zoom_used?: boolean;
  element_name?: string;
  spotlight_type?: string;
  promo_code?: string;
  favorites_skus?: string[];
  favorites_count?: number;
  phone_number_present?: boolean;
  sent_welcome_sms?: boolean;
  communicate_note?: string;
  primary_intent?: string;

  // utm props
  utm_campaign?: string;
  utm_medium?: string;
  utm_source?: string;
  utm_id?: string;
  utm_term?: string;
  utm_content?: string;
  utm_channel?: string;
  utm_adgroup?: string;
  utm_matchtype?: string;
  utm_device?: string;
  number_of_notifications_alerts?: number;
  full_url?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  li_fat_id?: string;
  destinations?: string[];
  biological_sex?: string;
  age_range?: string;
  condition?: string;
  labs_count?: number;
  oldest_labs_date?: string;

  // Videos
  timestamp?: number;
  video_url?: YouTubeNoCookieUrl | VimeoEmbedUrl | string;
  play_status?: YouTubePlayStatus | VimeoPlayStatus;

  // EMERSON
  days_since_order_placed?: number;
  estimated_ship_date_known?: boolean;
  multicart_type?: MultiCartType.ADMIN | MultiCartType.CUSTOMER;

  is_intake_store?: boolean;
  brand_names?: string[];
  all_added?: boolean;

  // fs.js props
  ehr_name?: string;
  integration_type?: string;
  rendered_height_px?: number;
  rendered_width_px?: number;

  // Labs stuff
  company_name?: string;
  includes_lab_test?: boolean;
  requisition_view_type?: "download" | "email" | "link";
  practice_patient?: boolean;
  panel_name?: string;
  lab_test_status?: string;
  lab_results_reviewed?: boolean;
  test_type?: string;
  company_ids?: string[];
  test_type_ids?: string[];
  sample_type_ids?: string[];
  health_category_ids?: string[];
  biomarker_ids?: string[];
  phlebotomy_not_required?: boolean;
  favorites_only?: boolean;
  test_frequency?: string;
  lab_journey_subscription_id?: string;
  lab_journey_package_id?: string;
  includes_lab_journey?: boolean;
  biomarker?: string;
  biomarker_category?: string;
  biomarker_score?: string;
  is_lab_journey?: boolean;
  is_partial_results?: boolean;
  practitioner_pay?: boolean;
  current_lab_company?: string;
  new_lab_company?: string;
  number_of_comparisons?: number;
  name_of_compared_companies?: string[];
  compared_lab_test_skus?: string[];
  zip_code?: string;
  journey?: string; // journey name
  interpretation_date?: string;
  journey_id?: string;
  price?: number;
  health_area?: string;
  entry_screen?: string;
  interval_type?: string;
  total_supplements?: number;
  total_resources?: number;
  practitioner_message?: boolean;
  practitioner_message_length?: number;
  action_plan_message?: boolean;
  action_plan_message_length?: number;
  practitioner_supplement_message?: boolean;
  resource_title?: string;
  flagged_biomarkers?: number;
  next_test_visible?: boolean;
  follow_up_test_visible?: boolean;
  appointment_link_present?: boolean;

  // Journey plan send event
  follow_up_lab_included?: boolean;
  panel_id?: string;
  interpretation_fee?: number;
  discount_percent?: number;
  requires_fasting?: boolean;
  note_present?: boolean;
  booking_link_present?: boolean;
  suggested_timing?: string;
  tests?: string;
  parent_journey?: boolean;

  passed_validation?: boolean;
  modification_types?: string[];
  number_of_plans?: number;
  content?: string;
  ingredient_name?: string;
  medication_name?: string;
  ingredient_or_medication_name?: string;
  biomarker_or_condition_name?: string;
  type?: string;
  common_type?: string;

  project?: "default" | "rupa_migration";
  scheduled_date?: string;

  // Document singing stuff
  document_type?: DocumentEventDocType;

  // Multi-Factor Authentication (MFA)
  two_fa_method?: string;

  full_plan?: boolean;
  selected_path?: string[];

  // search
  search_type?: SearchType;

  // notification hub
  notification_type?: string;

  // top nav back button
  navigation_type?: "history" | "hierarchy";

  // hub cta click
  banner_name?: string;
  link_path?: string;
  is_exit?: boolean;

  sharing_enabled?: boolean;
  format?: string;
  migration_token?: string;

  cta?: string;
  booking_link_domain?: string;
  sharing_level?: string;
  actions?: string[];

  // Platform Pay
  shipping_method_id?: string;

  // Store quality program settings
  program_type?: "Enhanced testing" | "Top pick";
  new_status?: "opt_in" | "opt_out";

  duration_on_going?: boolean;

  // collections
  product_count?: number;
};

enum SearchType {
  PRODUCT = "Product",
  INGREDIENT = "Ingredient",
  BRAND = "Brand",
  SUPPLEMENT_TYPE = "SupplementType",
  CONDITION = "Condition",
  QUERY = "query",
  PATIENT = "Patient",
  TAXONOMY = "Taxonomy",
  IS_FS_TESTED = "IsFsTested",
  FORM = "Form",
}

enum LabSearchType {
  TEST = "Test",
  COMPANY = "Company",
  HEALTH_CATEGORY = "HealthCategory",
  BIOMARKER = "Biomarker",
  SAMPLE_TYPE = "SampleType",
  QUERY = "query",
  LAB_KIT = "LabKit",
}

enum FilterType {
  CATEGORY = "category",
  BRAND = "brand",
  SUPPLEMENT_TYPE = "supplement_type",
  CONDITION = "condition",
  INCLUDE_INGREDIENT = "include_ingredient",
  EXCLUDE_INGREDIENT = "exclude_ingredient",
  AVAILABILITY = "availability",
  FAVORITES = "favorites",
  DEMOGRAPHIC = "demographic",
  FORM = "form",
  ALLERGENS = "allergens",
  CERTIFICATIONS = "certifications",
  PERSONAL_CARE = "personal_care",
  IS_FS_TESTED = "is_fs_tested",
  ON_SALE = "on_sale",
}

enum FilterSource {
  CATEGORY_PAGE = "CategoryPage",
  LEFT_PANEL = "LeftPanel",
}

enum LabFilterType {
  COMPANY = "company",
  HEALTH_CATEGORY = "health_category",
  SAMPLE_TYPE = "sample_type",
  PHLEBOTOMY_NOT_REQUIRED = "phlebotomy_not_required",
  CATEGORY = "category",
  FAVORITES = "favorites",
}

enum MapAgreementType {
  SINGLE = "Single",
  MULTI = "Multi",
}

enum CategoryType {
  FAVORITES = "Favorites",
  COLLECTIONS = "Collections",
}

interface AnalyticsSearchPayload extends Pick<
  AnalyticsPayload,
  | "entry_point"
  | "ehr_name"
  | "integration_type"
  | "rendered_height_px"
  | "rendered_width_px"
  | "search_version"
  | "practitioner_modality"
  | "practitioner_id"
  | "clerk_id"
> {
  content: string;
  type: SearchType | LabSearchType | string;
  query_id?: string;
}

interface AnalyticsFilterPayload extends Pick<
  AnalyticsPayload,
  | "entry_point"
  | "ehr_name"
  | "integration_type"
  | "rendered_height_px"
  | "rendered_width_px"
  | "search_version"
  | "practitioner_modality"
  | "practitioner_id"
  | "clerk_id"
> {
  content: string;
  filter_type: FilterType | LabFilterType;
}

interface AnalyticsOrderPayload extends Pick<
  AnalyticsPayload,
  "entry_point" | "ehr_name" | "integration_type" | "rendered_height_px" | "rendered_width_px"
> {
  split_shipment?: boolean;
  consolidated_order_status?: string;
}

interface AnalyticsEventProps {
  eventName: AnalyticsEvent;
  payload:
    | AnalyticsPayload
    | AnalyticsSearchPayload
    | AnalyticsFilterPayload
    | AnalyticsOrderPayload;
}

/**
 * `useAnalyticsLogger` is a hook that provides a function to log analytics events.
 *
 * @returns {(input: LogAnalyticsStepOptions | AnalyticsEventProps) => Promise<AnalyticsLogEventData>} logAnalyticsEvent - A function that logs analytics events.
 *
 * @example Using AnalyticsEventProps - mutation input only
 *   const logAnalyticsEvent = useAnalyticsLogger();
 *   await logAnalyticsEvent({
 *     eventName: AnalyticsEvent.STORE_PAYMENTS_SET_UP_PAYOUTS,
 *     payload: {
 *       margin: 25,
 *       account_discount: 10,
 *     },
 *   });
 *
 * @example Using LogAnalyticsStepOptions - complete mutation payload
 *   const logAnalyticsEvent = useAnalyticsLogger();
 *   void logAnalyticsEvent({
 *     variables: {
 *       input: {
 *         eventName: AnalyticsEvent.STORE_PAYMENTS_SET_UP_PAYOUTS,
 *         payload: {
 *           margin: 25,
 *           account_discount: 10,
 *         },
 *       },
 *     },
 *     onCompleted: () => navigate(url),
 *     onError: (err) => handleError(err),
 *   });
 */
const useAnalyticsLogger = () => {
  const ehrInfo = getEHRInfoState();
  const utmParams = useUnauthUrlFields();
  const fullStorySessionUrl = useFullStorySessionUrl();
  const [logAnalyticsStep] = useAnalyticsLogEventMutation({
    onError: error => {
      // eslint-disable-next-line no-console
      console.error("Error logging analytics event: ", error);
    },
  });

  type LogAnalyticsStepParams = Parameters<typeof logAnalyticsStep>;
  type LogAnalyticsStepOptions = LogAnalyticsStepParams[0];

  const logAnalyticsEvent = (input: AnalyticsEventProps | LogAnalyticsStepOptions) => {
    const payload = "eventName" in input ? { variables: { input } } : input;

    if (ehrInfo !== null) {
      payload.variables.input.payload.integration_type = ehrInfo.integrationType;
      payload.variables.input.payload.ehr_name = ehrInfo.ehrName;
    }

    if (fullStorySessionUrl) {
      payload.variables.input.payload.fullstory_session_url = fullStorySessionUrl;
    }

    payload.variables.input.payload = {
      ...payload.variables.input.payload,
      ...utmParams,
    };

    return logAnalyticsStep(payload);
  };

  return logAnalyticsEvent;
};

export {
  useAnalyticsLogger,
  SearchType,
  LabSearchType,
  FilterType,
  LabFilterType,
  MapAgreementType,
  FilterSource,
  CategoryType,
};
export type {
  AnalyticsEventProps,
  AnalyticsPayload,
  AnalyticsFilterPayload,
  AnalyticsOrderPayload,
  AnalyticsSearchPayload,
};
